import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import mineCraftApi from '../../api/mineCraftApi';
import { userEntity, usersEntity } from "./usersActions";
import { CommentInterface } from "../../store/mine_craft/commentsSlice";
import { UserInterface } from "../../store/mine_craft/usersSlice";
import minecraftUrls from "../../configs/mine_craft/minecraftUrls";

const processStrategy = (value: any) => {
  return {
    ...value,
    url: minecraftUrls.commentPath(value),
    comments: []
  };
}

export const commentsEntity = new schema.Entity(
  'comments',
  {
    sender: usersEntity
  },
  {
    processStrategy
  }
);

export const commentEntity = new schema.Entity(
  'comment',
  {
    sender: userEntity
  },
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any, {
    user: { [key: string]: UserInterface };
    comment: CommentInterface;
  }
  >(response.data.comment, commentEntity);
  return normalized.entities;
}

const commentRequest = async (params: object = {}) => {
  const response    = await mineCraftApi.get(`/onesky/comments`, { params })
  const normalized  = normalize<
    any, {
    users: { [key: string]: UserInterface };
    comments: { [key: string]: CommentInterface }
  }
    >(response.data.comments, [commentsEntity]);

  return {
    users: normalized.entities.users || {},
    comments: normalized.entities.comments || {},
    ids: normalized.result,
    count: response.data.meta?.total_count || 0
  }
}

export const fetchComments = createAsyncThunk(
  'comments/fetchComments',
  commentRequest
);

export const fetchUserComments = createAsyncThunk(
  'comments/fetchComments',
  async ({ userId, params }: { userId: number, params: object }) => {
    const response    = await mineCraftApi.get(`/onesky/users/${userId}/comments`, { params })
    const normalized  = normalize<
      any, {
      users: { [key: string]: UserInterface };
      comments: { [key: string]: CommentInterface }
    }
      >(response.data.comments, [commentsEntity]);

    return {
      users: normalized.entities.users || {},
      comments: normalized.entities.comments || {},
      ids: normalized.result,
      count: response.data.meta?.total_count || 0
    }
  }
);

export const fetchReplies = createAsyncThunk(
  'comments/fetchReplies',
  commentRequest
);

export const fetchComment = createAsyncThunk(
  'comments/fetchComment',
  async (id: number) => {
    const response = await mineCraftApi.get(`/onesky/comments/${id}`);
    return normalizeInstance(response);
  }
);

export const updateComment = createAsyncThunk(
  'comments/updateComment',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/comments/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyComment = createAsyncThunk(
  'comments/destroyComment',
  async (id: number) => {
    await mineCraftApi.delete(`/onesky/comments/${id}`);
    return { id: id };
  }
);
